import React from 'react';

/* ─── DateClicksChart ────────────────────────────────────────────────────── */
const RANGE_OPTIONS = [
    { label: '7d', days: 7 },
    { label: '14d', days: 14 },
    { label: '30d', days: 30 },
    { label: '90d', days: 90 },
];
const CHART_TYPES = [
    { key: 'bar', icon: '▌▌', label: 'Bar' },
    { key: 'line', icon: '╱', label: 'Line' },
    { key: 'area', icon: '◿', label: 'Area' },
];

const DateClicksChart = ({ data }) => {
    const [range, setRange] = React.useState(30);
    const [chartType, setChartType] = React.useState('bar');
    const [tooltip, setTooltip] = React.useState(null);

    // Build scaffold for selected range, filled with zeros
    const today = new Date();
    const days = Array.from({ length: range }, (_, i) => {
        const d = new Date(today);
        d.setDate(today.getDate() - (range - 1) + i);
        const key = d.toISOString().split('T')[0];
        return { date: key, label: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), clicks: 0 };
    });

    // Merge API rows (backend returns up to 90 days)
    (data || []).forEach(row => {
        const key = row.date ? row.date.split('T')[0] : null;
        const idx = days.findIndex(d => d.date === key);
        if (idx !== -1) days[idx].clicks = parseInt(row.clicks) || 0;
    });

    const n = days.length;
    const maxClicks = Math.max(...days.map(d => d.clicks), 1);
    const W = 800, H = 220, padL = 44, padR = 12, padT = 22, padB = 42;
    const chartW = W - padL - padR;
    const chartH = H - padT - padB;
    const xStep = chartW / n;
    const barW = Math.max(2, xStep - (n > 30 ? 1 : 3));

    // Helpers
    const px = i => padL + i * xStep + xStep / 2;
    const py = v => padT + chartH * (1 - v / maxClicks);

    // Line/area path
    const points = days.map((d, i) => `${px(i)},${py(d.clicks)}`).join(' ');
    const areaPath = `M${px(0)},${padT + chartH} ` +
        days.map((d, i) => `L${px(i)},${py(d.clicks)}`).join(' ') +
        ` L${px(n - 1)},${padT + chartH} Z`;

    // Y-axis grid lines
    const yLines = [0.25, 0.5, 0.75, 1].map(f => ({
        y: padT + chartH * (1 - f),
        label: Math.round(maxClicks * f),
    }));

    // X-axis labels — show ~6 evenly spaced
    const labelEvery = Math.max(1, Math.floor(n / 6));
    const xLabels = days.filter((_, i) => i % labelEvery === 0 || i === n - 1);

    const noData = !data || data.length === 0;

    return (
        <div style={{ position: 'relative', width: '100%' }}>
            {/* ── Controls row ── */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
                {/* Day range */}
                <div style={{ display: 'flex', gap: 4 }}>
                    {RANGE_OPTIONS.map(r => (
                        <button key={r.days} onClick={() => setRange(r.days)} style={{
                            padding: '4px 13px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                            border: 'none', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.18s',
                            background: range === r.days ? 'rgba(99,102,241,0.22)' : 'rgba(255,255,255,0.04)',
                            color: range === r.days ? '#a5b4fc' : '#475569',
                            outline: range === r.days ? '1px solid rgba(99,102,241,0.35)' : '1px solid transparent',
                        }}>{r.label}</button>
                    ))}
                </div>
                {/* Chart type */}
                <div style={{ display: 'flex', gap: 4 }}>
                    {CHART_TYPES.map(ct => (
                        <button key={ct.key} onClick={() => setChartType(ct.key)} title={ct.label} style={{
                            padding: '4px 13px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                            border: 'none', cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.18s',
                            background: chartType === ct.key ? 'rgba(99,102,241,0.22)' : 'rgba(255,255,255,0.04)',
                            color: chartType === ct.key ? '#a5b4fc' : '#475569',
                            outline: chartType === ct.key ? '1px solid rgba(99,102,241,0.35)' : '1px solid transparent',
                        }}>{ct.icon} {ct.label}</button>
                    ))}
                </div>
            </div>

            {/* ── Tooltip ── */}
            {tooltip && (
                <div style={{
                    position: 'absolute', left: tooltip.x, top: tooltip.y - 52,
                    transform: 'translateX(-50%)',
                    background: '#1e2a45', border: '1px solid rgba(99,102,241,0.35)',
                    borderRadius: 8, padding: '6px 12px',
                    fontSize: 12, fontWeight: 700, color: '#e2e8f0',
                    pointerEvents: 'none', whiteSpace: 'nowrap', zIndex: 10,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
                }}>
                    <span style={{ color: '#818cf8' }}>{tooltip.label}</span><br />
                    {tooltip.clicks} click{tooltip.clicks !== 1 ? 's' : ''}
                </div>
            )}

            {/* ── SVG chart ── */}
            <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block', overflow: 'visible' }}>
                <defs>
                    {/* Bar gradient */}
                    <linearGradient id="dcBarGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#6366f1" />
                        <stop offset="100%" stopColor="#4f46e5" stopOpacity="0.65" />
                    </linearGradient>
                    {/* Area gradient */}
                    <linearGradient id="dcAreaGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#6366f1" stopOpacity="0.45" />
                        <stop offset="100%" stopColor="#6366f1" stopOpacity="0.03" />
                    </linearGradient>
                </defs>

                {/* Y-grid */}
                {yLines.map((gl, i) => (
                    <g key={i}>
                        <line x1={padL} y1={gl.y} x2={W - padR} y2={gl.y}
                            stroke="rgba(99,102,241,0.1)" strokeWidth="1" strokeDasharray="4 4" />
                        <text x={padL - 6} y={gl.y + 4} textAnchor="end"
                            fill="#475569" fontSize="10" fontFamily="Inter, sans-serif">{gl.label}</text>
                    </g>
                ))}

                {/* ── Bar chart ── */}
                {chartType === 'bar' && days.map((d, i) => {
                    const bh = Math.max(2, (d.clicks / maxClicks) * chartH);
                    const bx = padL + i * xStep + (xStep - barW) / 2;
                    const by = padT + chartH - bh;
                    const hit = padL + i * xStep;
                    return (
                        <g key={d.date}>
                            <rect x={bx} y={by} width={barW} height={bh} rx={3}
                                fill={d.clicks === 0 ? 'rgba(51,65,85,0.4)' : 'url(#dcBarGrad)'} />
                            <rect x={hit} y={padT} width={xStep} height={chartH}
                                fill="transparent" style={{ cursor: 'crosshair' }}
                                onMouseEnter={e => {
                                    const r = e.currentTarget.closest('svg').getBoundingClientRect();
                                    setTooltip({ x: (bx + barW / 2) * (r.width / W), y: by * (r.height / H), label: d.label, clicks: d.clicks });
                                }}
                                onMouseLeave={() => setTooltip(null)} />
                        </g>
                    );
                })}

                {/* ── Area chart ── */}
                {chartType === 'area' && (
                    <>
                        <path d={areaPath} fill="url(#dcAreaGrad)" />
                        <polyline points={points} fill="none"
                            stroke="#6366f1" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
                        {days.map((d, i) => {
                            const cx = px(i), cy = py(d.clicks);
                            return (
                                <circle key={d.date} cx={cx} cy={cy} r={4}
                                    fill="#6366f1" stroke="#1e2a45" strokeWidth="2" style={{ cursor: 'crosshair' }}
                                    onMouseEnter={e => {
                                        const r = e.currentTarget.closest('svg').getBoundingClientRect();
                                        setTooltip({ x: cx * (r.width / W), y: cy * (r.height / H), label: d.label, clicks: d.clicks });
                                    }}
                                    onMouseLeave={() => setTooltip(null)} />
                            );
                        })}
                    </>
                )}

                {/* ── Line chart ── */}
                {chartType === 'line' && (
                    <>
                        <polyline points={points} fill="none"
                            stroke="#818cf8" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round"
                            strokeDasharray="0" />
                        {days.map((d, i) => {
                            const cx = px(i), cy = py(d.clicks);
                            return (
                                <circle key={d.date} cx={cx} cy={cy} r={3.5}
                                    fill={d.clicks === 0 ? '#334155' : '#6366f1'} stroke="#0a0f1e" strokeWidth="1.5"
                                    style={{ cursor: 'crosshair' }}
                                    onMouseEnter={e => {
                                        const r = e.currentTarget.closest('svg').getBoundingClientRect();
                                        setTooltip({ x: cx * (r.width / W), y: cy * (r.height / H), label: d.label, clicks: d.clicks });
                                    }}
                                    onMouseLeave={() => setTooltip(null)} />
                            );
                        })}
                    </>
                )}

                {/* X-axis baseline */}
                <line x1={padL} y1={padT + chartH} x2={W - padR} y2={padT + chartH}
                    stroke="rgba(99,102,241,0.2)" strokeWidth="1" />

                {/* X-axis labels */}
                {xLabels.map(d => {
                    const idx = days.findIndex(dd => dd.date === d.date);
                    return (
                        <text key={d.date} x={px(idx)} y={H - 6} textAnchor="middle"
                            fill="#475569" fontSize="10" fontFamily="Inter, sans-serif">{d.label}</text>
                    );
                })}
            </svg>

            {noData && (
                <div style={{ textAlign: 'center', color: '#334155', fontSize: 13, marginTop: -70, paddingBottom: 20 }}>
                    No click data yet for the last {range} days.
                </div>
            )}
        </div>
    );
};

export default DateClicksChart;
